import { Box, Button, Card, CardContent, Typography } from "@mui/material";

import { useSessionUser } from "../auth/AuthProvider.js";
import { PageLoader } from "../components/ui/page-loader.js";
import { NeedsAttention } from "./dashboard/NeedsAttention.js";
import { PulseHeader } from "./dashboard/PulseHeader.js";
import { useDashboardPulse } from "./dashboard/useDashboardPulse.js";

/**
 * "Company pulse" landing page. Composes:
 *  - PulseHeader     — compact one-row hero: greeting + mood-tinted
 *                       status sentence on the left, system-health
 *                       pills on the right, no metric tile grid.
 *  - NeedsAttention  — three columns (1 below 560px, 2 to 820px):
 *                       Customer care, Revenue & pipeline, and
 *                       Products & partners (licences expiring,
 *                       reseller applications, latest releases).
 *                       Every card is gated by its page's grant.
 *
 * The previous "Live activity" panel was removed — it was mostly a
 * re-mix of data already shown in the queues (and the server-events
 * page at `/server`), and on a typical tick it only carried a handful
 * of server-up/down rows that aren't useful in this surface.
 *
 * Polls `/api/dashboard/pulse` every 20s (see `useDashboardPulse`).
 *
 * Design lives in
 * `docs/superpowers/specs/2026-05-15-dashboard-pulse-redesign-design.md`.
 */
export function DashboardPage(): JSX.Element {
  const user = useSessionUser();
  const { data, isLoading, isError, error, refetch, isRefetching } =
    useDashboardPulse();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !data) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <PageHeaderFallback userFirstName={user.name.split(" ")[0] ?? "there"} />
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 14, color: "error.main" }}>
              Could not load company pulse:{" "}
              {error instanceof Error ? error.message : "unknown error"}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => void refetch()}
              sx={{ mt: 1.5 }}
            >
              Try again
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const firstName = user.name.split(" ")[0] ?? "there";

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="dashboard-pulse">
      <PulseHeader
        headline={data.headline}
        systems={data.systems}
        monitored_servers={data.monitored_servers}
        generated_at={data.generated_at}
        isRefetching={isRefetching}
        userFirstName={firstName}
      />

      <NeedsAttention
        queues={data.queues}
        upstreamErrors={data.upstream_errors}
        permissions={user.permissions}
        role={user.dashboard_role}
      />

      {data.upstream_errors.length > 0 && (
        <Box
          component="details"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "surfaceSoft",
            px: 1.5,
            py: 1,
            fontSize: 12,
            color: "text.disabled",
          }}
        >
          <Box component="summary" sx={{ cursor: "pointer", userSelect: "none" }}>
            {data.upstream_errors.length} upstream source
            {data.upstream_errors.length === 1 ? "" : "s"} returned an error this
            tick — page rendered with the rest
          </Box>
          <Box component="ul" sx={{ mt: 1, display: "grid", gap: 0.5, pl: 2, m: 0 }}>
            {data.upstream_errors.map((err) => (
              <li key={err.source}>
                <Box component="span" sx={{ fontFamily: "ui-monospace, monospace" }}>
                  {err.source}
                </Box>
                : {err.message}
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PageHeaderFallback({
  userFirstName,
}: {
  userFirstName: string;
}): JSX.Element {
  return (
    <Box component="header" sx={{ display: "grid", gap: 0.5 }}>
      <Typography
        component="h1"
        sx={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em", color: "text.primary" }}
      >
        Hey {userFirstName} — here&apos;s what&apos;s happening.
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        Live numbers from every system you have access to.
      </Typography>
    </Box>
  );
}
